import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Card, Row, Col, Alert, Badge } from 'react-bootstrap';
import api from '../../api/axios';

const ShopUserManagementPage = () => {
    const { shopId } = useParams();
    const [users, setUsers] = useState([]);
    const [shopName, setShopName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 비밀번호 변경 모달 상태
    const [showPwModal, setShowPwModal] = useState(false);
    const [selectedUser, setSelectedResUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [pwModalError, setPwModalError] = useState('');

    const fetchUsers = useCallback(async () => {
        try {
            const response = await api.get(`/superadmin/users?shop_id=${shopId}`);
            setUsers(response.data.data);
            // 매장 목록에서 이름을 찾기 위해 추가 호출 (선택사항)
            const shopRes = await api.get('/superadmin/shops');
            const currentShop = shopRes.data.data.find(s => s.id === parseInt(shopId));
            if (currentShop) setShopName(currentShop.shopName);
        } catch (err) {
            setError('사용자 목록을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    }, [shopId]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleToggleStatus = async (userId, currentStatus) => {
        if (window.confirm(`계정 상태를 ${currentStatus ? '비활성' : '활성'}으로 변경하시겠습니까?`)) {
            try {
                await api.put(`/superadmin/users/${userId}/status`, { isActive: !currentStatus });
                fetchUsers();
            } catch (err) { alert('상태 변경에 실패했습니다.'); }
        }
    };

    const handleShowPwModal = (user) => {
        setSelectedResUser(user);
        setNewPassword('');
        setPwModalError('');
        setShowPwModal(true);
    };

    const handleResetPassword = async () => {
        if (newPassword.length < 6) {
            setPwModalError('비밀번호는 6자 이상이어야 합니다.');
            return;
        }
        try {
            await api.put(`/superadmin/users/${selectedUser.id}/password`, { newPassword });
            alert(`${selectedUser.username}님의 비밀번호가 변경되었습니다.`);
            setShowPwModal(false);
        } catch (err) {
            setPwModalError('비밀번호 변경에 실패했습니다.');
        }
    };

    if (loading) return <div className="text-center py-5">로딩 중...</div>;

    return (
        <div className="container-fluid py-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-2">
                            <li className="breadcrumb-item"><Link to="/superadmin/shops">매장 관리</Link></li>
                            <li className="breadcrumb-item active">{shopName || '매장'} 사용자 관리</li>
                        </ol>
                    </nav>
                    <h3 className="mb-0">[{shopName}] 계정 관리</h3>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Table hover responsive className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>아이디</th>
                                <th>역할</th>
                                <th>상태</th>
                                <th>생성일</th>
                                <th className="text-end">작업</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id}>
                                    <td className="fw-bold">{u.username}</td>
                                    <td>
                                        <Badge bg={u.role === 'admin' ? 'primary' : 'info'}>
                                            {u.role === 'admin' ? '관리자' : '직원'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={u.isActive ? 'success' : 'secondary'}>
                                            {u.isActive ? '사용 중' : '정지됨'}
                                        </Badge>
                                    </td>
                                    <td className="text-muted small">{new Date(u.created_at).toLocaleDateString()}</td>
                                    <td className="text-end">
                                        <Button 
                                            variant="outline-warning" 
                                            size="sm" 
                                            className="me-2"
                                            onClick={() => handleShowPwModal(u)}
                                        >
                                            비밀번호 변경
                                        </Button>
                                        <Button 
                                            variant={u.isActive ? "outline-danger" : "outline-success"} 
                                            size="sm"
                                            onClick={() => handleToggleStatus(u.id, u.isActive)}
                                        >
                                            {u.isActive ? '정지' : '복구'}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* 비밀번호 변경 모달 */}
            <Modal show={showPwModal} onHide={() => setShowPwModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>비밀번호 강제 재설정</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-3">
                        <strong>{selectedUser?.username}</strong> 계정의 새로운 비밀번호를 입력하세요.
                    </p>
                    {pwModalError && <Alert variant="danger" className="py-2 small">{pwModalError}</Alert>}
                    <Form.Group>
                        <Form.Label className="fw-bold">새 비밀번호</Form.Label>
                        <Form.Control 
                            type="password" 
                            value={newPassword} 
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="6자 이상 입력"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPwModal(false)}>취소</Button>
                    <Button variant="warning" onClick={handleResetPassword}>비밀번호 변경 실행</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShopUserManagementPage;
